import React from 'react';
import { motion } from 'framer-motion';
import { Search, ShoppingBag, Phone } from 'lucide-react';
import { staggerContainer, fadeUp } from '../../animations/variants';

const steps = [
  {
    step: '01',
    icon: Search,
    title: 'Browse & Select',
    desc: 'Find the right compression stocking or brace by brand, type, color, and size. See real-time stock levels for every option.',
    color: 'bg-ndms-navy',
  },
  {
    step: '02',
    icon: ShoppingBag,
    title: 'Add to Cart',
    desc: 'Add your chosen items to the cart. No account needed — just select your products and quantities.',
    color: 'bg-ndms-blue',
  },
  {
    step: '03',
    icon: Phone,
    title: 'We Fulfill Your Order',
    desc: 'Submit with your name, email, and phone. Our team receives your order, prepares it, and emails you the moment it’s ready to pick up.',
    color: 'bg-ndms-teal',
  },
];

export function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-20 sm:py-28 bg-ndms-cream">
      <div className="container-max section-padding">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          className="text-center mb-14"
        >
          <motion.p variants={fadeUp} className="text-ndms-blue font-semibold text-sm uppercase tracking-widest mb-3">
            Simple Process
          </motion.p>
          <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-display font-bold text-ndms-navy mb-4">
            How Ordering Works
          </motion.h2>
          <motion.p variants={fadeUp} className="text-gray-600 max-w-xl mx-auto text-lg">
            No complicated checkout. No account required. Just browse, add to cart, and our team handles the rest.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 relative"
        >
          {/* Connector line */}
          <div className="hidden md:block absolute top-14 left-1/3 right-1/3 h-0.5 bg-gradient-to-r from-ndms-navy via-ndms-blue to-ndms-teal opacity-20" />

          {steps.map(({ step, icon: Icon, title, desc, color }, i) => (
            <motion.div
              key={step}
              variants={fadeUp}
              custom={i}
              className="relative text-center"
            >
              <div className={`w-20 h-20 ${color} rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-lg`}>
                <Icon size={32} className="text-white" />
              </div>
              <div className="absolute top-0 right-1/2 translate-x-8 -translate-y-2 w-7 h-7 bg-white border-2 border-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-500 shadow">
                {step}
              </div>
              <h3 className="text-xl font-bold text-ndms-navy mb-3">{title}</h3>
              <p className="text-gray-600 leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mt-14 text-center"
        >
          <div className="inline-flex items-center gap-3 bg-white rounded-2xl px-6 py-4 shadow-card border border-gray-100">
            <Phone size={20} className="text-ndms-teal" />
            <p className="text-gray-700 text-sm">
              <span className="font-semibold">Questions?</span> Call us at{' '}
              <a href="tel:+13069794115" className="text-ndms-blue font-semibold hover:underline">
                +1 (306) 979-4115
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
